import React, { useMemo, useState } from 'react';

import { format } from 'date-fns';
import { useTranslation } from 'react-i18next';
import { generatePath } from 'react-router-dom';

import FooterButton from 'components/FooterButton';
import Table from 'components/Table';
import UserAvatar from 'components/UserAvatar/UserAvatar';
import { organizationRiskAnalysesApiLegacy } from 'config/api/organizationRiskAnalyses/organizationRiskAnalyses';
import { parseOrganizationRiskAnalysesForFE } from 'config/api/organizationRiskAnalyses/organizationRiskAnalyses.parsers';
import { OrganizationRiskAnalyses } from 'config/api/organizationRiskAnalyses/organizationRiskAnalyses.types';
import DATE_FORMAT from 'config/constants/DATE_FORMAT';
import safeT from 'helpers/safeT/safeT';
import usePagination from 'hooks/usePagination';
import asset_risk_messages from 'messages/asset_risk_messages';
import assets_messages from 'messages/assets_messages';
import general_messages from 'messages/general_messages';
import PATHS from 'router/PATHS';
import parseFiltersForRequest from 'services/parseFiltersForRequest';
import { TableDataMappingRow } from 'types/Table';

import { StaticTableFilter } from '../../../../types/TableFilter';
import RiskQuadrantCell from '../../../SingleRiskAnalysisPage/_components/RiskQuadrantCell/RiskQuadrantCell';

type Props = {
  filters: Record<string, any>;
  setFilters: (filters: Record<string, any>) => void;
  availableFilters?: Record<string, StaticTableFilter>;
};

const RiskAnalysisDashboardTable: React.FC<Props> = ({ filters, setFilters, availableFilters }) => {
  const { t } = useTranslation();
  const [sort, setSort] = useState<{ key: string | null; direction: string | null }>({ key: null, direction: null });
  const onSort = (key: string, direction: string) => setSort({ key, direction });

  const {
    data: tableData,
    getData: getTableData,
    hasNextPage,
    loading,
  } = usePagination({
    endpointFunc: organizationRiskAnalysesApiLegacy.getOrganizationRiskAnalyses,
    limits: [15, 10, 5],
    isGet: true,
    dependencies: [filters],
    sort,
    filters: parseFiltersForRequest(filters, {}, availableFilters || {}),
  });

  const dataMapping: TableDataMappingRow<OrganizationRiskAnalyses>[] = useMemo(
    () => [
      {
        id: 'asset__name',
        label: t(asset_risk_messages.analysis_table.asset_name_column),
        get: ({ asset }) => safeT(t, asset.key, asset.name) || t(general_messages.no_data),
        width: '13%',
      },
      {
        id: 'asset__category__translated_name',
        label: t(asset_risk_messages.analysis_table.asset_type_column),
        get: ({ asset }) => safeT(t, asset.category.nameTranslationKey, asset.category.name) || t(general_messages.no_data),
        width: '13%',
      },
      {
        id: 'asset__owner__full_name',
        label: t(asset_risk_messages.analysis_table.asset_owner_column),
        get: ({ asset }) => <UserAvatar lazy showName userData={asset.owner.profile} userId={asset.owner.id} />,
        width: '17%',
      },
      {
        id: 'date',
        label: t(asset_risk_messages.analysis_table.last_analysis_column),
        get: ({ date }) => format(date, DATE_FORMAT.defaultDate) || t(general_messages.no_data),
        width: '9%',
      },
      {
        id: 'status',
        label: t(asset_risk_messages.analysis_table.status_column),
        get: ({ status }) => t(asset_risk_messages.status[status]) || t(general_messages.no_data),
        width: '9%',
      },
      {
        id: 'asset__info__assets_importance',
        label: t(asset_risk_messages.analysis_table.importance_column),
        get: ({ asset }) =>
          t(assets_messages.information_choices_assets_importance[asset.info.assetsImportance]) || t(general_messages.no_data),
        width: '11%',
      },
      {
        id: 'asset__info__contains_sensitive_individual_information',
        label: t(asset_risk_messages.analysis_table.gdpr_column),
        get: ({ asset }) => (asset.info.containsSensitiveIndividualInformation ? t(general_messages.yes) : t(general_messages.no)),
        width: '10%',
      },
      {
        id: 'highest_risk_quadrant',
        label: t(asset_risk_messages.analysis_table.highest_risk_quadrant_column),
        get: ({ highestRiskQuadrant }) =>
          <RiskQuadrantCell hideDetails riskQuadrant={highestRiskQuadrant} /> || t(general_messages.no_data),
        width: '9%',
      },
      {
        id: 'risks_to_handle',
        label: t(asset_risk_messages.analysis_table.risk_to_handle_column),
        get: ({ risksToHandleNumber }) => risksToHandleNumber,
        width: '9%',
      },
    ],
    [tableData],
  );

  const getRowHref = (row: OrganizationRiskAnalyses) => generatePath(PATHS.RISK_ANALYSIS_DETAIL, { riskAnalysisId: row.id });

  return (
    <>
      <Table
        clickableRow
        data={tableData && parseOrganizationRiskAnalysesForFE(tableData)}
        dataMapping={dataMapping}
        filtersService={availableFilters && { availableFilters, filters, setFilters }}
        getRowHref={getRowHref}
        onSort={onSort}
        openInNewTab
        refetching={loading}
        sort={sort}
      />
      {hasNextPage && (
        // @ts-ignore
        <FooterButton loading={loading} onClick={getTableData}>
          {t(general_messages.show_more)}
        </FooterButton>
      )}
    </>
  );
};

export default RiskAnalysisDashboardTable;
